import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Divider, FormControlLabel, LinearProgress, Paper, Switch, Typography } from '@mui/material';
import { formatBytes, IUsage, UsageService } from '../../services/usage.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { helpWhy } from '../../help/why';

const percent = (used: number, cap: number): number => (cap > 0 ? Math.min(100, (used / cap) * 100) : 0);

interface MeterProps {
    label: string;
    used: number;
    cap: number;
    valueText: string;
}

const Meter: React.FC<MeterProps> = ({ label, used, cap, valueText }) => {
    const pct = percent(used, cap);
    const over = used >= cap;
    return (
        <Box sx={{ flex: '1 1 260px', maxWidth: 480 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>{label}: {valueText}</Typography>
            <LinearProgress
                variant="determinate"
                value={pct}
                color={over ? 'error' : pct >= 80 ? 'warning' : 'primary'}
                aria-label={label}
                sx={{ height: 10, borderRadius: 5 }}
            />
        </Box>
    );
};

/**
 * "Usage & limits" card for the institution settings page. Hidden if the usage cannot be read (the caller
 * lacks the settings-view key). Payroll, attendance, leave and every read, report and export are never blocked.
 */
const UsageLimitsCard: React.FC<{ institutionId: string }> = ({ institutionId }) => {
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.INSTITUTION_SETTINGS_MANAGE);
    const [usage, setUsage] = useState<IUsage | null>(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            setUsage(await UsageService.getUsage(institutionId));
        } catch {
            setUsage(null);
        }
    }, [institutionId]);

    useEffect(() => {
        load();
    }, [load]);

    if (!usage) return null;

    const continueMode = usage.overageMode === 'CONTINUE';

    const handleToggle = async (checked: boolean) => {
        try {
            setSaving(true);
            setUsage(await UsageService.updatePolicy(institutionId, checked ? 'CONTINUE' : 'BLOCK'));
            toast.showSuccess(checked ? 'Pay-as-you-go turned on' : 'Limits will stop new postings and uploads');
        } catch (error) {
            toast.showApiError(error, 'Failed to save usage policy');
        } finally {
            setSaving(false);
        }
    };

    const switchWhy = saving ? null
        : usage.overageModeLocked ? 'The Free plan always stops at its limit. Upgrade to a paid plan to choose pay-as-you-go.'
        : !canManage ? 'You need the permission to manage institution settings to change this.'
        : null;

    return (
        <Paper sx={{ p: 3, mt: 3 }} data-help-section="institutions.usage.section">
            <Typography variant="h6" sx={{ mb: 2 }}>Usage &amp; limits</Typography>
            <Divider sx={{ mb: 2 }} />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Plan: <strong>{usage.planName}</strong> - usage for {usage.month}. Transactions are journal entries
                posted this month; storage is every uploaded file.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                <Meter
                    label="Transactions this month"
                    used={usage.transactionsUsed}
                    cap={usage.transactionCap}
                    valueText={`${usage.transactionsUsed.toLocaleString()} of ${usage.transactionCap.toLocaleString()}`}
                />
                <Meter
                    label="Storage"
                    used={usage.storageUsedBytes}
                    cap={usage.storageCapBytes}
                    valueText={`${formatBytes(usage.storageUsedBytes)} of ${formatBytes(usage.storageCapBytes)}`}
                />
            </Box>

            <FormControlLabel
                data-help="institutions.usage.payAsYouGo"
                {...helpWhy(switchWhy ? { kind: 'status', message: switchWhy } : null)}
                control={
                    <Switch
                        checked={continueMode}
                        disabled={usage.overageModeLocked || saving || !canManage}
                        onChange={(e) => handleToggle(e.target.checked)}
                    />
                }
                label="Keep working past the limit (pay as you go)"
                sx={{ mb: 1 }}
            />

            {!canManage && !usage.overageModeLocked && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Only an administrator who can manage institution settings can change this.
                </Typography>
            )}

            {usage.overageModeLocked ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                    The Free plan always stops at its limit. Upgrade to a paid plan to choose pay-as-you-go.
                </Alert>
            ) : continueMode ? (
                <Typography variant="body2" color="text.secondary">
                    Continue: you keep working past the limit and any overage is billed on your next invoice.
                    Overage so far this month: <strong>৳{usage.overageTaka.toLocaleString()}</strong>.
                </Typography>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    Block: when a limit is reached, new postings and uploads stop until next month or until you
                    upgrade. Payroll, attendance, leave, reports and exports are never blocked.
                </Typography>
            )}
        </Paper>
    );
};

export default UsageLimitsCard;
